import { PlusIcon } from '@acceso/design-system';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { usePostFeaturedPeople } from '@/features/featured-people/api/post-featured-people';
import { useFeaturedPeoplePermissions } from '@/features/featured-people/hooks/useFeaturedPeoplePermissions';
import { useSaveDeclarantAsPeople } from '@/features/featured-people/hooks/useSaveDeclarantAsPeople';
import { TFeaturedPeopleForm, TFeaturedPeopleType } from '@/features/featured-people/types';
import { usePatchSegmentDeclaration } from '@/features/segments/api/patch-segment-declaration';
import { usePostSegmentDeclaration } from '@/features/segments/api/post-segment-declaration';
import { TPatchSegmentDeclaration, TPostSegmentDeclaration } from '@/features/segments/types';
import { useDisclosureWithData } from '@/hooks/useDisclosureWithData';

import { DECLARATION_FORM_DEFAULT_VALUES } from '../constants';
import { TBroadcastScheduleProgramBodySegmentToolkitDeclarationForm } from '../types';

export function useBroadcastScheduleProgramBodySegmentToolkitDeclarationFormState() {
  const {
    declarationData,
    formBox,
    activeSegment,
    selectedMention,
    selectedMentionTime,
    setLogoData,
    setFormBox,
    setActiveSegment,
    drawnFormReset,
    setSegmentItems,
  } = useBroadcastScheduleProgram();
  const postSegmentDeclaration = usePostSegmentDeclaration();
  const patchSegmentDeclaration = usePatchSegmentDeclaration();
  const form = useForm<TBroadcastScheduleProgramBodySegmentToolkitDeclarationForm>({
    mode: 'all',
    defaultValues: DECLARATION_FORM_DEFAULT_VALUES,
  });
  const peopleDrawer = useDisclosureWithData();
  const { t } = useTranslation();
  const { mutateAsync: createPeople, isPending: isCreatingPeople } = usePostFeaturedPeople();
  const { canCreate: canCreatePeople } = useFeaturedPeoplePermissions();
  const { linkDeclarant, isSaving: isSavingPeople } = useSaveDeclarantAsPeople();

  const id = form.watch('id');

  const [savePersonAsPeople, setSavePersonAsPeople] = useState(false);

  const renderAdditionalButtons = () => {
    return [
      {
        label: t('Crear nueva persona de interés'),
        icon: PlusIcon,
        onClick: () => peopleDrawer.open(),
      },
    ];
  };

  useEffect(() => {
    if (formBox !== 'declaration') return;

    if (selectedMention && selectedMentionTime) {
      form.setValue('start_datetime', new Date(selectedMentionTime));
      form.setValue('text', selectedMention);
    }
  }, [selectedMention, selectedMentionTime, form, formBox]);

  const handleSubmitPeople = useCallback(
    async (data: TFeaturedPeopleForm): Promise<TFeaturedPeopleType> => {
      try {
        const result = await createPeople(data);
        peopleDrawer.close();
        return result;
      } catch (error) {
        console.error('Error during mutation', error);
        throw error;
      }
    },
    [createPeople]
  );

  const handleSubmit = async (data: TBroadcastScheduleProgramBodySegmentToolkitDeclarationForm) => {
    // Abort the declaration when the declarant could not be saved: persisting it
    // anyway would silently fall back to an orphaned record.
    const declarationData = await linkDeclarant(data, savePersonAsPeople);
    if (!declarationData) return;

    const formatedData: TPostSegmentDeclaration | TPatchSegmentDeclaration = {
      ...declarationData,
      segment_id: activeSegment?.id || '',
      end_datetime: declarationData.start_datetime,
    };
    if (id) {
      handleUpdate(formatedData as TPatchSegmentDeclaration);
    } else {
      handleCreate(formatedData as TPostSegmentDeclaration);
    }
  };

  const handleCreate = async (formatedData: TPostSegmentDeclaration) => {
    try {
      const item = await postSegmentDeclaration.mutateAsync(formatedData);
      setActiveSegment(prev => {
        if (prev) {
          return {
            ...prev,
            segment_declarations: [...(prev.segment_declarations || []), item],
          };
        }
        return prev;
      });
      setSegmentItems(prev =>
        prev.map(segment => {
          if (segment.id === formatedData?.segment_id) {
            return {
              ...segment,
              segment_declarations: [...(segment.segment_declarations || []), item],
            };
          }
          return segment;
        })
      );
      drawnFormReset();
    } catch (error) {
      console.error('Error al crear el people:', error);
    }
  };

  const handleUpdate = async (formatedData: TPatchSegmentDeclaration) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { segment_id, ...restData } = formatedData;
    const body: TPatchSegmentDeclaration = restData;
    try {
      const item = await patchSegmentDeclaration.mutateAsync({
        id,
        body,
      });
      setActiveSegment(prev => {
        if (prev) {
          return {
            ...prev,
            segment_declarations: prev.segment_declarations?.map(declaration => {
              if (declaration.id === item.id) {
                return item;
              }
              return declaration;
            }),
          };
        }
        return prev;
      });
      setSegmentItems(prev =>
        prev.map(segment => {
          if (segment.id === formatedData?.segment_id) {
            return {
              ...segment,
              segment_declarations: (segment.segment_declarations || []).map(ocr => {
                if (ocr.id === item.id) {
                  return item;
                }
                return ocr;
              }),
            };
          }
          return segment;
        })
      );
      drawnFormReset();
    } catch (error) {
      console.error('Error al actualizar la persona de interés:', error);
    }
  };

  const handleClickExit = useCallback(() => {
    form.reset(DECLARATION_FORM_DEFAULT_VALUES);
    drawnFormReset();
  }, [setFormBox, setLogoData]);

  useEffect(() => {
    if (formBox !== 'declaration') return;

    if (declarationData?.id) {
      form.reset({
        id: declarationData.id,
        text: declarationData.text,
        people_id: declarationData.people_id,
        start_datetime: new Date(declarationData.start_datetime),
        review_state: declarationData.review_state,
        entity_label: declarationData.entity_label,
      });
    }
  }, [declarationData]);

  return {
    form,
    isCreatingPeople,
    peopleDrawer,
    canCreatePeople,
    savePersonAsPeople,
    setSavePersonAsPeople,
    isSavingPeople,
    renderAdditionalButtons,
    handleSubmitPeople,
    handleSubmit,
    handleClickExit,
  };
}
